import axios, { isAxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import type {
  ProcessInventoryPayload,
  ProcessInventoryResponse,
  PurchaseOrderItem,
  PurchaseOrderItemsResponse,
} from '@/domain/inventory';
import { VITE_URL_SERVICE } from '@/utils/envs';

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: {
    Accept: 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

const cleanMessage = (msg: string) => msg.replace(/^(\[.*?\]\s*)+/, '').trim();

const getPoLoadErrorMessage = (status: number | undefined): string => {
  if (status === 400) return 'Verifica el número de PO';
  if (status === 404) return 'Orden de compra no encontrada';
  if (status === 500) return 'No se pudo cargar la PO, intenta de nuevo';
  return 'No se pudo cargar la PO, intenta de nuevo';
};

export const useReception = () => {
  const [poId, setPoId] = useState('');
  const [loadedPoId, setLoadedPoId] = useState('');
  const [sku, setSku] = useState('');
  const [poItems, setPoItems] = useState<PurchaseOrderItem[]>([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [isLoadingPo, setIsLoadingPo] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const serialInputRef = useRef<HTMLInputElement>(null);

  const resetPoState = useCallback(() => {
    setLoadedPoId('');
    setSku('');
    setPoItems([]);
    setSerialNumber('');
  }, []);

  const fetchPoItems = useCallback(async (purchaseOrder: string) => {
    const { data } = await httpPublic.get<PurchaseOrderItemsResponse>(
      '/api/v3/ops-izzi/purchase-orders/items',
      { params: { purchaseOrder } },
    );
    return data;
  }, []);

  const onLoadPo = useCallback(async () => {
    const trimmedPo = poId.trim();
    if (!trimmedPo) {
      toast.warning('Ingresa el identificador de PO.');
      return;
    }

    setIsLoadingPo(true);
    resetPoState();
    setPoId(trimmedPo);

    try {
      const data = await fetchPoItems(trimmedPo);
      setLoadedPoId(trimmedPo);
      setPoItems(data.items);

      if (data.items.length === 0) {
        toast.warning('La PO no tiene productos disponibles para escanear.');
        return;
      }

      if (data.items.length === 1) {
        setSku(data.items[0].sku);
      }

      setTimeout(() => serialInputRef.current?.focus(), 50);
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      toast.error(getPoLoadErrorMessage(status));
      resetPoState();
      setPoId(trimmedPo);
    } finally {
      setIsLoadingPo(false);
    }
  }, [poId, fetchPoItems, resetPoState]);

  const refreshPoItems = useCallback(async () => {
    if (!loadedPoId) return;

    try {
      const data = await fetchPoItems(loadedPoId);
      setPoItems(data.items);

      if (sku && !data.items.some((item) => item.sku === sku)) {
        setSku(data.items.length === 1 ? data.items[0].sku : '');
      }
    } catch {
      // Silencioso: el escaneo ya fue procesado; el conteo se actualizará en el próximo load manual
    }
  }, [loadedPoId, sku, fetchPoItems]);

  const onProcessScan = async () => {
    if (!loadedPoId || !sku.trim()) {
      toast.warning('Carga la PO y selecciona un SKU antes de escanear.');
      serialInputRef.current?.focus();
      return;
    }

    if (!serialNumber.trim()) {
      return;
    }

    setIsValidating(true);

    try {
      const payload: ProcessInventoryPayload = {
        purchaseOrder: loadedPoId,
        sku: sku.trim(),
        iccid: serialNumber.trim(),
      };

      const { data } = await httpPublic.post<ProcessInventoryResponse>(
        '/api/v3/ops-izzi/validate-serial',
        payload,
      );

      if (data.status === 'REJECTED' || data.status === 'ERROR') {
        const cleanMsg = cleanMessage(data.message || '');
        throw new Error(
          cleanMsg || 'Hay un error con este número de serie, separa la pieza de inventario',
        );
      }

      if (data.status === 'QUARANTINED') {
        toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
      } else {
        toast.success('Número de serie procesado exitosamente.');
      }

      await refreshPoItems();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const data = error.response.data as ProcessInventoryResponse;
        if (data?.status === 'QUARANTINED') {
          toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
          await refreshPoItems();
          return;
        }
      }

      const rawAxiosError = isAxiosError(error) ? error.response?.data?.message : null;
      const axiosError = rawAxiosError ? cleanMessage(String(rawAxiosError)) : null;
      toast.error(
        axiosError ||
          (error instanceof Error ? error.message : 'Error desconocido al procesar el código.'),
      );
    } finally {
      setSerialNumber('');
      setIsValidating(false);
      setTimeout(() => serialInputRef.current?.focus(), 50);
    }
  };

  const onKeyDownSerialNumber = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessScan();
    }
  };

  const onKeyDownPoId = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onLoadPo();
    }
  };

  const onBlurPoId = () => {
    const trimmed = poId.trim();
    if (trimmed && trimmed !== loadedPoId) {
      onLoadPo();
    }
  };

  const onChangePoId = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPoId(value);

    if (loadedPoId && value.trim() !== loadedPoId) {
      resetPoState();
    }
  };

  const onChangeSku = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSku(event.target.value);
  };

  const onChangeSerialNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSerialNumber(event.target.value);
  };

  const isPoLoaded = loadedPoId.length > 0 && poItems.length > 0;
  const canScan = isPoLoaded && !!sku && !isLoadingPo && !isValidating;

  return {
    poId,
    onChangePoId,
    onKeyDownPoId,
    onBlurPoId,
    onLoadPo,
    sku,
    onChangeSku,
    poItems,
    serialNumber,
    onChangeSerialNumber,
    isLoadingPo,
    isValidating,
    isPoLoaded,
    canScan,
    serialInputRef,
    onKeyDownSerialNumber,
  };
};
